// Turn-Mill Live Tooling Operations and G-code Generation
// Supports X, Z, C, Y, and B axis live tooling

// Specific machine presets
export const machinePresets = {
  'samsung-sl25': {
    name: 'Samsung SL25/500',
    controller: 'fanuc',
    controllerModel: 'FANUC Series 0i-TD',
    type: 'lathe',
    axes: ['X', 'Z'],
    hasLiveTooling: false,
    hasCAxis: false,
    hasYAxis: false,
    hasBAxis: false,
    hasTailstock: false,
    hasSubSpindle: false,
    maxRPM: 4500,
    maxBarDia: 2.5,
    maxTurningDia: 14.2,
    maxTurningLength: 19.7,
    turretStations: 12,
    description: 'Samsung SL25/500 - 2-Axis CNC Turning Center, FANUC 0i-TD'
  },
  'hyundai-2600y': {
    name: 'Hyundai 2600Y',
    controller: 'fanuc',
    controllerModel: 'FANUC 0i-TF Plus',
    type: 'lathe-turnmill',
    axes: ['X', 'Z', 'C', 'Y', 'B'],
    hasLiveTooling: true,
    hasCAxis: true,
    hasYAxis: true,
    hasBAxis: true,
    hasTailstock: true,
    hasSubSpindle: false,
    maxRPM: 4500,
    liveToolRPM: 6000,
    maxBarDia: 3.0,
    maxTurningDia: 14.5,
    maxTurningLength: 23.6,
    turretStations: 12,
    yAxisTravel: 4.13,
    description: 'Hyundai 2600Y - Turn/Mill with Tailstock, Live Tooling (X/Z/C/Y/B)'
  },
  'hyundai-5600hd': {
    name: 'Hyundai 5600HD',
    controller: 'fanuc',
    controllerModel: 'FANUC 0i-MF Plus',
    type: 'mill',
    axes: ['X', 'Y', 'Z'],
    hasLiveTooling: false,
    hasCAxis: false,
    hasYAxis: true,
    hasBAxis: false,
    hasTailstock: false,
    hasSubSpindle: false,
    maxRPM: 8000,
    tableSize: '49.2 x 21.7',
    xTravel: 40.0,
    yTravel: 21.0,
    zTravel: 20.0,
    toolMagazine: 24,
    description: 'Hyundai 5600HD - Vertical Machining Center'
  },
};

// Machine configurations for Turn-Mill
export const turnMillConfig = {
  '2axis': {
    name: '2-Axis Lathe',
    axes: ['X', 'Z'],
    hasLiveTooling: false,
    hasCAxis: false,
    hasYAxis: false,
    hasBAxis: false,
    hasTailstock: false,
    description: 'Standard 2-axis turning center',
    preset: 'samsung-sl25',
  },
  '2axis-c': {
    name: '2-Axis + C (Live Tooling)',
    axes: ['X', 'Z', 'C'],
    hasLiveTooling: true,
    hasCAxis: true,
    hasYAxis: false,
    hasBAxis: false,
    description: 'Turning with C-axis positioning and driven tools'
  },
  '3axis-y': {
    name: 'Y-Axis Turn/Mill (X/Z/C/Y)',
    axes: ['X', 'Y', 'Z', 'C'],
    hasLiveTooling: true,
    hasCAxis: true,
    hasYAxis: true,
    hasBAxis: false,
    description: 'Full mill-turn capability with Y-axis'
  },
  '5axis-turnmill': {
    name: '5-Axis Turn/Mill (X/Z/C/Y/B)',
    axes: ['X', 'Y', 'Z', 'C', 'B'],
    hasLiveTooling: true,
    hasCAxis: true,
    hasYAxis: true,
    hasBAxis: true,
    hasTailstock: true,
    description: 'Complete turn-mill with B-axis tilt',
    preset: 'hyundai-2600y',
  },
};

// Live tooling operation types
export const liveToolingOperations = {
  // Axial Operations (on face - Z axis direction)
  'axial-drill': {
    name: 'Axial Drill',
    type: 'axial',
    description: 'Drill hole in face of part',
    requiresCAxis: true,
    requiresYAxis: false,
    fields: [
      { id: 'cAngle', label: 'C Angle (deg)', default: 0 },
      { id: 'xOffset', label: 'X Position (in)', default: 0 },
      { id: 'depth', label: 'Depth (in)', default: 0.5 },
      { id: 'drillDia', label: 'Drill Diameter (in)', default: 0.25 },
    ]
  },
  'axial-tap': {
    name: 'Axial Tap',
    type: 'axial',
    description: 'Tap threaded hole in face',
    requiresCAxis: true,
    requiresYAxis: false,
    fields: [
      { id: 'cAngle', label: 'C Angle (deg)', default: 0 },
      { id: 'xOffset', label: 'X Position (in)', default: 0 },
      { id: 'depth', label: 'Depth (in)', default: 0.375 },
      { id: 'pitch', label: 'Pitch (TPI)', default: 20 },
    ]
  },
  'axial-mill-slot': {
    name: 'Axial Slot (Face)',
    type: 'axial',
    description: 'Mill slot on face using C-axis rotation',
    requiresCAxis: true,
    requiresYAxis: false,
    fields: [
      { id: 'startAngle', label: 'Start Angle (deg)', default: 0 },
      { id: 'endAngle', label: 'End Angle (deg)', default: 90 },
      { id: 'radius', label: 'Radius from Center (in)', default: 0.5 },
      { id: 'depth', label: 'Depth (in)', default: 0.125 },
      { id: 'toolDia', label: 'End Mill Dia (in)', default: 0.25 },
    ]
  },
  'axial-pocket': {
    name: 'Axial Pocket (Face)',
    type: 'axial',
    description: 'Mill pocket on face (requires Y-axis)',
    requiresCAxis: true,
    requiresYAxis: true,
    fields: [
      { id: 'centerX', label: 'Center X (in)', default: 0 },
      { id: 'centerY', label: 'Center Y (in)', default: 0 },
      { id: 'width', label: 'Width (in)', default: 0.5 },
      { id: 'length', label: 'Length (in)', default: 0.75 },
      { id: 'depth', label: 'Depth (in)', default: 0.125 },
      { id: 'toolDia', label: 'End Mill Dia (in)', default: 0.25 },
    ]
  },
  'axial-contour': {
    name: 'Axial Contour (Polar)',
    type: 'axial',
    description: 'Mill contour using G12.1 polar interpolation',
    requiresCAxis: true,
    requiresYAxis: false,
    fields: [
      { id: 'shape', label: 'Shape', default: 'hex', options: ['hex', 'square', 'custom'] },
      { id: 'size', label: 'Across Flats (in)', default: 0.75 },
      { id: 'depth', label: 'Depth (in)', default: 0.125 },
      { id: 'toolDia', label: 'End Mill Dia (in)', default: 0.25 },
    ]
  },

  // Radial Operations (on OD - perpendicular to axis)
  'radial-drill': {
    name: 'Radial Drill (Cross Hole)',
    type: 'radial',
    description: 'Drill hole perpendicular to axis',
    requiresCAxis: true,
    requiresYAxis: false,
    fields: [
      { id: 'cAngle', label: 'C Angle (deg)', default: 0 },
      { id: 'zPosition', label: 'Z Position (in)', default: -0.5 },
      { id: 'depth', label: 'Depth (in)', default: 0.25 },
      { id: 'drillDia', label: 'Drill Diameter (in)', default: 0.125 },
      { id: 'partDia', label: 'Part Diameter (in)', default: 1.0 },
    ]
  },
  'radial-tap': {
    name: 'Radial Tap (Cross Hole)',
    type: 'radial',
    description: 'Tap threaded cross hole',
    requiresCAxis: true,
    requiresYAxis: false,
    fields: [
      { id: 'cAngle', label: 'C Angle (deg)', default: 0 },
      { id: 'zPosition', label: 'Z Position (in)', default: -0.5 },
      { id: 'depth', label: 'Depth (in)', default: 0.25 },
      { id: 'pitch', label: 'Pitch (TPI)', default: 24 },
      { id: 'partDia', label: 'Part Diameter (in)', default: 1.0 },
    ]
  },
  'radial-slot': {
    name: 'Radial Slot (OD)',
    type: 'radial',
    description: 'Mill slot along Z-axis on OD',
    requiresCAxis: true,
    requiresYAxis: false,
    fields: [
      { id: 'cAngle', label: 'C Angle (deg)', default: 0 },
      { id: 'zStart', label: 'Z Start (in)', default: -0.25 },
      { id: 'zEnd', label: 'Z End (in)', default: -1.0 },
      { id: 'depth', label: 'Depth into OD (in)', default: 0.125 },
      { id: 'toolDia', label: 'End Mill Dia (in)', default: 0.25 },
      { id: 'partDia', label: 'Part Diameter (in)', default: 1.0 },
    ]
  },
  'radial-flat': {
    name: 'Radial Flat (OD)',
    type: 'radial',
    description: 'Mill flat on OD (requires Y-axis for width)',
    requiresCAxis: true,
    requiresYAxis: true,
    fields: [
      { id: 'cAngle', label: 'C Angle (deg)', default: 0 },
      { id: 'zStart', label: 'Z Start (in)', default: -0.25 },
      { id: 'zEnd', label: 'Z End (in)', default: -1.0 },
      { id: 'flatDepth', label: 'Flat Depth (in)', default: 0.1 },
      { id: 'flatWidth', label: 'Flat Width (in)', default: 0.5 },
      { id: 'toolDia', label: 'End Mill Dia (in)', default: 0.375 },
      { id: 'partDia', label: 'Part Diameter (in)', default: 1.0 },
    ]
  },
  'radial-keyway': {
    name: 'Radial Keyway',
    type: 'radial',
    description: 'Mill keyway slot on OD',
    requiresCAxis: true,
    requiresYAxis: false,
    fields: [
      { id: 'cAngle', label: 'C Angle (deg)', default: 0 },
      { id: 'zStart', label: 'Z Start (in)', default: -0.25 },
      { id: 'length', label: 'Keyway Length (in)', default: 0.75 },
      { id: 'width', label: 'Keyway Width (in)', default: 0.125 },
      { id: 'depth', label: 'Keyway Depth (in)', default: 0.0625 },
      { id: 'partDia', label: 'Part Diameter (in)', default: 0.5 },
    ]
  },

  // Special Operations
  'spiral-groove': {
    name: 'Spiral/Helix Groove',
    type: 'special',
    description: 'Mill spiral groove using C+Z interpolation',
    requiresCAxis: true,
    requiresYAxis: false,
    fields: [
      { id: 'startZ', label: 'Start Z (in)', default: 0 },
      { id: 'endZ', label: 'End Z (in)', default: -2.0 },
      { id: 'pitch', label: 'Pitch (in/rev)', default: 0.5 },
      { id: 'grooveDepth', label: 'Groove Depth (in)', default: 0.0625 },
      { id: 'toolDia', label: 'End Mill Dia (in)', default: 0.125 },
      { id: 'partDia', label: 'Part Diameter (in)', default: 1.0 },
    ]
  },
  'polygon-turn': {
    name: 'Polygon Turning',
    type: 'special',
    description: 'Generate polygon (hex, square) on OD',
    requiresCAxis: true,
    requiresYAxis: false,
    fields: [
      { id: 'sides', label: 'Number of Sides', default: 6 },
      { id: 'acrossFlats', label: 'Across Flats (in)', default: 0.75 },
      { id: 'zStart', label: 'Z Start (in)', default: -0.25 },
      { id: 'zEnd', label: 'Z End (in)', default: -1.25 },
      { id: 'stockDia', label: 'Stock Diameter (in)', default: 1.0 },
    ]
  },

  // B-axis / Angular Operations
  'angular-drill': {
    name: 'Angular Drill (B-axis)',
    type: 'angular',
    description: 'Drill at angle using B-axis tilt',
    requiresCAxis: true,
    requiresYAxis: false,
    requiresBAxis: true,
    fields: [
      { id: 'cAngle', label: 'C Angle (deg)', default: 0 },
      { id: 'bAngle', label: 'B Angle (deg)', default: 30 },
      { id: 'zPosition', label: 'Z Position (in)', default: -0.5 },
      { id: 'depth', label: 'Depth (in)', default: 0.375 },
      { id: 'drillDia', label: 'Drill Diameter (in)', default: 0.125 },
      { id: 'partDia', label: 'Part Diameter (in)', default: 1.0 },
    ]
  },
  'angular-mill': {
    name: 'Angular Mill (B-axis)',
    type: 'angular',
    description: 'Mill feature at compound angle',
    requiresCAxis: true,
    requiresYAxis: false,
    requiresBAxis: true,
    fields: [
      { id: 'cAngle', label: 'C Angle (deg)', default: 0 },
      { id: 'bAngle', label: 'B Angle (deg)', default: 45 },
      { id: 'zPosition', label: 'Z Position (in)', default: -0.5 },
      { id: 'depth', label: 'Depth (in)', default: 0.125 },
      { id: 'width', label: 'Cut Width (in)', default: 0.375 },
      { id: 'toolDia', label: 'End Mill Dia (in)', default: 0.125 },
      { id: 'partDia', label: 'Part Diameter (in)', default: 1.0 },
    ]
  },

  // Multi-hole patterns for live tooling
  'radial-drill-pattern': {
    name: 'Radial Drill Pattern (Cross)',
    type: 'radial',
    description: 'Multiple cross holes equally spaced around OD',
    requiresCAxis: true,
    requiresYAxis: false,
    fields: [
      { id: 'numHoles', label: 'Number of Holes', default: 4 },
      { id: 'startAngle', label: 'Start C Angle (deg)', default: 0 },
      { id: 'zPosition', label: 'Z Position (in)', default: -0.5 },
      { id: 'depth', label: 'Depth (in)', default: 0.25 },
      { id: 'drillDia', label: 'Drill Diameter (in)', default: 0.125 },
      { id: 'partDia', label: 'Part Diameter (in)', default: 1.0 },
    ]
  },
  'axial-drill-pattern': {
    name: 'Axial Drill Pattern (Bolt Circle)',
    type: 'axial',
    description: 'Drill bolt circle pattern on face',
    requiresCAxis: true,
    requiresYAxis: false,
    fields: [
      { id: 'numHoles', label: 'Number of Holes', default: 6 },
      { id: 'boltCircleDia', label: 'Bolt Circle Diameter (in)', default: 1.5 },
      { id: 'startAngle', label: 'Start C Angle (deg)', default: 0 },
      { id: 'depth', label: 'Depth (in)', default: 0.375 },
      { id: 'drillDia', label: 'Drill Diameter (in)', default: 0.1875 },
    ]
  },
  // Cross milling with X-axis live tool
  'radial-cross-mill': {
    name: 'Cross Mill (X-axis Live Tool)',
    type: 'radial',
    description: 'Mill across OD using X-axis live tool holder',
    requiresCAxis: true,
    requiresYAxis: false,
    fields: [
      { id: 'cAngle', label: 'C Angle (deg)', default: 0 },
      { id: 'zStart', label: 'Z Start (in)', default: -0.25 },
      { id: 'zEnd', label: 'Z End (in)', default: -1.0 },
      { id: 'depth', label: 'Cut Depth (in)', default: 0.100 },
      { id: 'stepdown', label: 'Step Down (in)', default: 0.025 },
      { id: 'toolDia', label: 'End Mill Dia (in)', default: 0.125 },
      { id: 'partDia', label: 'Part Diameter (in)', default: 1.0 },
    ]
  },
  // Face milling with Z-axis live tool
  'axial-face-mill': {
    name: 'Face Mill (Z-axis Live Tool)',
    type: 'axial',
    description: 'Mill features on part face using Z-axis live tool',
    requiresCAxis: true,
    requiresYAxis: false,
    fields: [
      { id: 'startAngle', label: 'Start C Angle (deg)', default: 0 },
      { id: 'endAngle', label: 'End C Angle (deg)', default: 180 },
      { id: 'radius', label: 'Radius from Center (in)', default: 0.375 },
      { id: 'depth', label: 'Cut Depth (in)', default: 0.050 },
      { id: 'stepdown', label: 'Step Down (in)', default: 0.015 },
      { id: 'toolDia', label: 'End Mill Dia (in)', default: 0.125 },
    ]
  },
};

// M-codes for live tooling
export const liveToolingMCodes = {
  fanuc: {
    cAxisEngage: 'M91',      // Engage C-axis (lock spindle for positioning)
    cAxisDisengage: 'M90',   // Return to turning mode
    liveToolCW: 'M83',       // Live tool clockwise
    liveToolCCW: 'M84',      // Live tool counter-clockwise
    liveToolStop: 'M85',     // Live tool stop
    mainSpindleCW: 'M03',    // Main spindle CW
    mainSpindleCCW: 'M04',   // Main spindle CCW
    mainSpindleStop: 'M05',  // Main spindle stop
    coolantOn: 'M08',
    coolantOff: 'M09',
    highPressureCoolant: 'M68',
    highPressureCoolantOff: 'M69',
    feedPerMin: 'G98',       // Feed per minute (for milling)
    feedPerRev: 'G99',       // Feed per revolution (for turning)
    polarOn: 'G12.1',        // Polar interpolation ON
    polarOff: 'G13.1',       // Polar interpolation OFF
    cylindricalOn: 'G107',   // Cylindrical interpolation ON (for radial milling)
  },
  siemens: {
    cAxisEngage: 'SPOS=',
    cAxisDisengage: 'M5',
    liveToolCW: 'M103',
    liveToolCCW: 'M104',
    liveToolStop: 'M105',
    mainSpindleCW: 'M3',
    mainSpindleCCW: 'M4',
    mainSpindleStop: 'M5',
    coolantOn: 'M8',
    coolantOff: 'M9',
    feedPerMin: 'G94',
    feedPerRev: 'G95',
    polarOn: 'TRANSMIT',
    polarOff: 'TRAFOOF',
  },
  haas: {
    cAxisEngage: 'M154',     // C-axis clamp
    cAxisDisengage: 'M155',  // C-axis unclamp
    liveToolCW: 'M133',      // Live tool CW
    liveToolCCW: 'M134',     // Live tool CCW
    liveToolStop: 'M135',    // Live tool stop
    mainSpindleCW: 'M03',
    mainSpindleCCW: 'M04',
    mainSpindleStop: 'M05',
    coolantOn: 'M08',
    coolantOff: 'M09',
    feedPerMin: 'G98',
    feedPerRev: 'G99',
    polarOn: 'G112',         // Polar interpolation
    polarOff: 'G113',
  },
};

// Generate live tooling G-code
export const generateLiveToolingCode = (operation, params, settings, controller = 'fanuc') => {
  const mCodes = liveToolingMCodes[controller] || liveToolingMCodes.fanuc;
  const lines = [];
  const opDef = liveToolingOperations[operation];
  
  if (!opDef) return lines;

  const {
    tool = 10,
    liveToolSpeed = 3000,
    feed = 5.0,
    safeZ = 0.5,
  } = settings;

  // Header comment
  lines.push(`(${opDef.name.toUpperCase()})`);
  lines.push(`(LIVE TOOLING OPERATION)`);
  lines.push('');

  // Engage C-axis mode
  lines.push(mCodes.cAxisEngage);
  lines.push(mCodes.feedPerMin);
  lines.push('');

  // Tool call for live tool
  lines.push(`T${String(tool).padStart(2, '0')}${String(tool).padStart(2, '0')}`);
  lines.push(`${mCodes.liveToolCW} S${liveToolSpeed}`);
  lines.push(mCodes.coolantOn);
  lines.push('');

  // Safe position
  lines.push(`G00 X${(params.partDia || 2) + 1} Z${safeZ}`);
  lines.push('');

  // Operation-specific code
  switch (operation) {
    case 'axial-drill':
      lines.push(`(AXIAL DRILL AT C${params.cAngle} X${params.xOffset})`);
      lines.push(`G00 C${params.cAngle}`);
      lines.push(`X${params.xOffset * 2} Z0.1`);
      lines.push(`G83 Z-${params.depth} Q0.1 R0.05 F${feed}`);
      lines.push('G80');
      break;

    case 'axial-tap':
      lines.push(`(AXIAL TAP AT C${params.cAngle})`);
      const tapFeed = (liveToolSpeed / params.pitch).toFixed(4);
      lines.push(`G00 C${params.cAngle}`);
      lines.push(`X${params.xOffset * 2} Z0.1`);
      lines.push(`G84 Z-${params.depth} R0.05 F${tapFeed}`);
      lines.push('G80');
      break;

    case 'axial-mill-slot':
      lines.push(`(AXIAL SLOT FROM C${params.startAngle} TO C${params.endAngle})`);
      lines.push(mCodes.polarOn);
      lines.push(`G00 X${params.radius * 2} C${params.startAngle}`);
      lines.push(`Z0.1`);
      lines.push(`G01 Z-${params.depth} F${feed / 2}`);
      lines.push(`C${params.endAngle} F${feed}`);
      lines.push(`G00 Z${safeZ}`);
      lines.push(mCodes.polarOff);
      break;

    case 'axial-contour':
      lines.push(`(AXIAL CONTOUR - ${params.shape.toUpperCase()})`);
      lines.push(mCodes.polarOn);
      if (params.shape === 'hex') {
        const hexRadius = params.size / Math.sqrt(3);
        lines.push(`G00 X${hexRadius * 2} C0`);
        lines.push(`Z0.1`);
        lines.push(`G01 Z-${params.depth} F${feed / 2}`);
        lines.push(`G42 X${(hexRadius + params.toolDia/2) * 2} C0 F${feed}`);
        for (let i = 0; i < 6; i++) {
          const angle = i * 60;
          lines.push(`C${angle + 60}`);
        }
        lines.push('G40');
      } else if (params.shape === 'square') {
        const sqHalf = params.size / 2;
        lines.push(`G00 X${sqHalf * 2} C45`);
        lines.push(`Z0.1`);
        lines.push(`G01 Z-${params.depth} F${feed / 2}`);
        lines.push(`G42 C45 F${feed}`);
        lines.push(`C135`);
        lines.push(`C225`);
        lines.push(`C315`);
        lines.push(`C45`);
        lines.push('G40');
      }
      lines.push(`G00 Z${safeZ}`);
      lines.push(mCodes.polarOff);
      break;

    case 'radial-drill':
      lines.push(`(RADIAL DRILL AT C${params.cAngle} Z${params.zPosition})`);
      lines.push(`G00 C${params.cAngle}`);
      lines.push(`Z${params.zPosition}`);
      lines.push(`X${params.partDia + 0.2}`);
      lines.push(`G83 X${params.partDia - params.depth * 2} Q0.05 R${params.partDia + 0.1} F${feed}`);
      lines.push('G80');
      lines.push(`G00 X${params.partDia + 1}`);
      break;

    case 'radial-tap':
      lines.push(`(RADIAL TAP AT C${params.cAngle} Z${params.zPosition})`);
      const radialTapFeed = (liveToolSpeed / params.pitch).toFixed(4);
      lines.push(`G00 C${params.cAngle}`);
      lines.push(`Z${params.zPosition}`);
      lines.push(`X${params.partDia + 0.2}`);
      lines.push(`G84 X${params.partDia - params.depth * 2} R${params.partDia + 0.1} F${radialTapFeed}`);
      lines.push('G80');
      lines.push(`G00 X${params.partDia + 1}`);
      break;

    case 'radial-slot':
      lines.push(`(RADIAL SLOT AT C${params.cAngle} FROM Z${params.zStart} TO Z${params.zEnd})`);
      lines.push(`G00 C${params.cAngle}`);
      lines.push(`Z${params.zStart + 0.1}`);
      lines.push(`X${params.partDia + 0.2}`);
      lines.push(`G01 X${params.partDia - params.depth * 2} F${feed / 2}`);
      lines.push(`Z${params.zEnd} F${feed}`);
      lines.push(`G00 X${params.partDia + 1}`);
      break;

    case 'radial-keyway':
      lines.push(`(KEYWAY AT C${params.cAngle})`);
      lines.push(`G00 C${params.cAngle}`);
      lines.push(`Z${params.zStart + 0.1}`);
      lines.push(`X${params.partDia + 0.2}`);
      lines.push(`G01 X${params.partDia - params.depth * 2} F${feed / 2}`);
      lines.push(`Z${params.zStart - params.length} F${feed}`);
      lines.push(`G00 X${params.partDia + 1}`);
      break;

    case 'spiral-groove':
      lines.push(`(SPIRAL GROOVE - ${params.pitch}" PITCH)`);
      const turns = Math.abs(params.endZ - params.startZ) / params.pitch;
      const totalC = turns * 360;
      lines.push(`G00 C0`);
      lines.push(`Z${params.startZ + 0.1}`);
      lines.push(`X${params.partDia + 0.2}`);
      lines.push(`G01 X${params.partDia - params.grooveDepth * 2} F${feed / 2}`);
      lines.push(`Z${params.endZ} C${totalC} F${feed}`);
      lines.push(`G00 X${params.partDia + 1}`);
      break;

    case 'polygon-turn':
      lines.push(`(${params.sides}-SIDED POLYGON)`);
      lines.push(mCodes.polarOn);
      const angleStep = 360 / params.sides;
      const polyRadius = params.acrossFlats / (2 * Math.cos(Math.PI / params.sides));
      lines.push(`G00 Z${params.zStart + 0.1}`);
      lines.push(`X${polyRadius * 2 + 0.2} C0`);
      lines.push(`G01 X${polyRadius * 2} Z${params.zStart} F${feed / 2}`);
      for (let i = 0; i <= params.sides; i++) {
        lines.push(`C${i * angleStep} F${feed}`);
      }
      lines.push(`Z${params.zEnd}`);
      for (let i = params.sides; i >= 0; i--) {
        lines.push(`C${i * angleStep}`);
      }
      lines.push(`G00 X${params.stockDia + 1}`);
      lines.push(mCodes.polarOff);
      break;

    default:
      lines.push('(CUSTOM LIVE TOOLING OPERATION)');
  }

  lines.push('');
  
  // Return to safe position
  lines.push(`G00 Z${safeZ}`);
  lines.push(`X${(params.partDia || 2) + 1}`);
  lines.push('');

  // Stop live tool and coolant
  lines.push(mCodes.liveToolStop);
  lines.push(mCodes.coolantOff);
  lines.push('');

  // Return to turning mode
  lines.push(mCodes.cAxisDisengage);
  lines.push(mCodes.feedPerRev);

  return lines;
};

// Group operations by category for UI display
export const liveToolingCategories = {
  axial: {
    name: 'Axial (Face)',
    description: 'Operations on the face of the part (Z-axis direction)',
    ops: ['axial-drill', 'axial-tap', 'axial-mill-slot', 'axial-pocket', 'axial-contour', 'axial-face-mill', 'axial-drill-pattern'],
  },
  radial: {
    name: 'Radial (OD)',
    description: 'Operations perpendicular to the axis (cross holes, flats, keyways)',
    ops: ['radial-drill', 'radial-tap', 'radial-slot', 'radial-flat', 'radial-keyway', 'radial-cross-mill', 'radial-drill-pattern'],
  },
  angular: {
    name: 'Angular (B-axis)',
    description: 'Compound angle operations using B-axis tilt',
    ops: ['angular-drill', 'angular-mill'],
  },
  special: {
    name: 'Special',
    description: 'Spiral grooves, polygon turning, and other special operations',
    ops: ['spiral-groove', 'polygon-turn'],
  },
};

// Filter operations based on machine capabilities
export const getAvailableOperations = (machineConfig) => {
  const config = turnMillConfig[machineConfig] || turnMillConfig['2axis'];
  const available = {};
  
  Object.entries(liveToolingOperations).forEach(([key, op]) => {
    if (op.requiresBAxis && !config.hasBAxis) return;
    if (op.requiresYAxis && !config.hasYAxis) return;
    if (op.requiresCAxis && !config.hasCAxis) return;
    available[key] = op;
  });
  
  return available;
};

export default {
  machinePresets,
  turnMillConfig,
  liveToolingOperations,
  liveToolingCategories,
  liveToolingMCodes,
  generateLiveToolingCode,
  getAvailableOperations,
};
